import styled from '@emotion/styled';
import Image from 'next/image';

import { screen } from '@/components/common/breakpoints';
import { Divider } from '@/components/common/Divider';
import { Paragraph } from '@/components/common/MarkUp';
import { PortableText } from '@/components/common/utils';
import { Stack } from '@/components/layout/Stack';
import { bodyMedium, bodySmall } from '@/styles/typography';

import type { SanityBlock, SanityKeyed } from '@/types/sanity';
import type { BlockWithAudience } from '@/types/shared';

export interface ProgrammeCertificateProps extends BlockWithAudience {
  badgeType?: 'ribbon' | 'stamp';
  title?: string;
  caption?: string;
  body?: Array<SanityKeyed<SanityBlock>>;
  footer?: Array<SanityKeyed<SanityBlock>>;
}

export const ProgrammeCertificate = ({
  badgeType,
  caption,
  title,
  body,
  footer,
  pageAudience,
  isAudienceSwitcherEnabled,
}: ProgrammeCertificateProps) => {
  return (
    <ProgrammeCertificateContainer>
      <ContentContainer>
        {(caption || title || body) && (
          <Content spacing="--space-component-sm">
            {caption && <Caption size="label-sm">{caption}</Caption>}
            {title && <Name size="label-lg">{title}</Name>}
            {body && (
              <Description>
                <PortableText
                  value={body}
                  audienceConfig={{ pageAudience, isAudienceSwitcherEnabled }}
                />
              </Description>
            )}
          </Content>
        )}
        {!!badgeType && (
          <BadgeContainer>
            {badgeType === 'ribbon' ? (
              <Image
                width="64"
                height="88"
                src="/images/certificate-badge-ribbon-large.png"
                alt=""
              />
            ) : (
              <Image
                width="64"
                height="88"
                src="/images/certificate-badge-stamp-large.png"
                alt=""
              />
            )}
          </BadgeContainer>
        )}
      </ContentContainer>
      {footer && (
        <>
          {(body || title || caption) && (
            <Divider
              orientation="horizontal"
              thickness="2px"
              colour="--border-warm-subtle"
            />
          )}
          <Footer>
            <PortableText
              value={footer}
              audienceConfig={{ pageAudience, isAudienceSwitcherEnabled }}
            />
          </Footer>
        </>
      )}
    </ProgrammeCertificateContainer>
  );
};

const ProgrammeCertificateContainer = styled.div`
  display: flex;
  flex-direction: column;
  background: var(--surface-warm-base);
  border-radius: var(--radius-component-lg);

  // stops underlining any links in the certificate:
  p > a {
    text-decoration: none;
    color: var(--text-action);
    :visited {
      color: var(--text-action);
    }
  }
`;

const Footer = styled.div`
  padding: var(--space-component-md) var(--space-component-xl);
  text-align: center;

  > p {
    ${bodySmall}
  }
`;
const Content = styled(Stack)`
  padding: var(--space-component-xl) var(--space-component-lg)
    var(--space-component-lg) var(--space-component-xl);
`;

const Description = styled.div`
  p {
    ${bodyMedium}
  }
`;

const ContentContainer = styled.div`
  display: flex;
`;

const Caption = styled(Paragraph)`
  color: var(--text-warm-subtle);
`;

const Name = styled(Paragraph)`
  font-weight: var(--font-weight-semibold);
`;

const BadgeContainer = styled.div`
  margin-right: var(--space-component-xl);
  > img {
    width: 56px;
    height: 76px;
  }
  ${screen.lg} {
    > img {
      width: 64px;
      height: 88px;
    }
  }
`;
